import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { fmtEUR } from "../../lib/format";
import { Search, FileText, Users, LayoutDashboard, Sun, SlidersHorizontal, ArrowRight } from "lucide-react";

const NAV_ITEMS = [
  { label: "Briefing du jour", to: "/briefing", icon: Sun },
  { label: "Vue dirigeant", to: "/dashboard", icon: LayoutDashboard },
  { label: "Propales", to: "/propales", icon: FileText },
  { label: "Clients", to: "/clients", icon: Users },
  { label: "Réglages", to: "/reglages", icon: SlidersHorizontal },
];

export default function CommandPalette({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ propales: [], clients: [] });
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (open) {
      setQ("");
      setResults({ propales: [], clients: [] });
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      if (!q.trim()) {
        setResults({ propales: [], clients: [] });
        return;
      }
      try {
        const r = await api.get("/search", { params: { q } });
        setResults(r.data);
        setActive(0);
      } catch { /* silencieux */ }
    }, 150);
    return () => clearTimeout(t);
  }, [q, open]);

  const navFiltered = q ? NAV_ITEMS.filter((n) => n.label.toLowerCase().includes(q.toLowerCase())) : NAV_ITEMS;
  const items = [
    ...navFiltered.map((n) => ({ kind: "nav", ...n })),
    ...results.propales.map((p) => ({ kind: "propale", ...p })),
    ...results.clients.map((c) => ({ kind: "client", ...c })),
  ];

  const goto = (it) => {
    onClose();
    if (it.kind === "nav") nav(it.to);
    else if (it.kind === "propale") nav("/propales");
    else if (it.kind === "client") nav("/clients");
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (items[active]) goto(items[active]); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  if (!open) return null;

  const rowCls = (isActive) =>
    `flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer`;
  const rowStyle = (isActive) => ({
    backgroundColor: isActive ? "var(--th-surface-soft)" : "transparent",
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]"
      style={{ backgroundColor: "rgba(26, 43, 69, 0.5)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="cmdk-overlay"
    >
      <div
        className="w-full max-w-2xl"
        style={{
          backgroundColor: "var(--th-surface)",
          border: "1px solid var(--th-border)",
          borderRadius: 12,
          boxShadow: "0 24px 48px rgba(26, 43, 69, 0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center gap-3 px-4 py-3"
          style={{ borderBottom: "1px solid var(--th-border)" }}
        >
          <Search size={16} style={{ color: "var(--th-text-3)" }} />
          <input
            ref={inputRef}
            data-testid="cmdk-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Rechercher un client, une propale, un écran…"
            className="flex-1 outline-none text-sm bg-transparent"
            style={{ color: "var(--th-text)" }}
          />
          <span
            className="text-[10px]"
            style={{
              border: "1px solid var(--th-border-strong)",
              padding: "1px 6px",
              borderRadius: 4,
              color: "var(--th-text-3)",
            }}
          >
            ESC
          </span>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm" style={{ color: "var(--th-text-3)" }}>
              {q ? "Aucun résultat" : "Tapez pour rechercher"}
            </div>
          )}
          {items.map((it, i) => {
            const isActive = i === active;
            if (it.kind === "nav") {
              const Icon = it.icon;
              return (
                <div
                  key={`n-${it.to}`}
                  className={rowCls(isActive)}
                  style={rowStyle(isActive)}
                  onClick={() => goto(it)}
                  onMouseEnter={() => setActive(i)}
                >
                  <Icon size={14} style={{ color: "var(--th-text-3)" }} />
                  <span className="flex-1" style={{ color: "var(--th-text)" }}>{it.label}</span>
                  <span className="text-xs" style={{ color: "var(--th-text-3)" }}>Navigation</span>
                  <ArrowRight size={12} style={{ color: "var(--th-text-3)" }} />
                </div>
              );
            }
            if (it.kind === "propale") {
              return (
                <div
                  key={`p-${it.id}`}
                  className={rowCls(isActive)}
                  style={rowStyle(isActive)}
                  onClick={() => goto(it)}
                  onMouseEnter={() => setActive(i)}
                >
                  <FileText size={14} style={{ color: "var(--th-text-3)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate" style={{ color: "var(--th-text)" }}>{it.client} — {it.numero}</div>
                    <div className="text-xs truncate" style={{ color: "var(--th-text-3)" }}>
                      {fmtEUR(it.montant_ht)} · {it.statut} · envoyée le {it.date_envoi}
                    </div>
                  </div>
                  <span className="text-xs" style={{ color: "var(--th-text-3)" }}>Propale</span>
                </div>
              );
            }
            return (
              <div
                key={`c-${it.id}`}
                className={rowCls(isActive)}
                style={rowStyle(isActive)}
                onClick={() => goto(it)}
                onMouseEnter={() => setActive(i)}
              >
                <Users size={14} style={{ color: "var(--th-text-3)" }} />
                <div className="flex-1 min-w-0">
                  <div className="truncate" style={{ color: "var(--th-text)" }}>{it.nom}</div>
                  <div className="text-xs truncate" style={{ color: "var(--th-text-3)" }}>
                    {it.contact_nom} · {it.contact_email}
                  </div>
                </div>
                <span className="text-xs" style={{ color: "var(--th-text-3)" }}>Client</span>
              </div>
            );
          })}
        </div>
        <div
          className="px-4 py-2 flex items-center gap-4 text-xs"
          style={{ borderTop: "1px solid var(--th-border)", color: "var(--th-text-3)" }}
        >
          <span>
            <span
              style={{
                border: "1px solid var(--th-border-strong)",
                padding: "1px 5px",
                borderRadius: 4,
              }}
            >
              ↑↓
            </span>{" "}
            naviguer
          </span>
          <span>
            <span
              style={{
                border: "1px solid var(--th-border-strong)",
                padding: "1px 5px",
                borderRadius: 4,
              }}
            >
              ↵
            </span>{" "}
            ouvrir
          </span>
          <span className="ml-auto">Cmd/Ctrl + K</span>
        </div>
      </div>
    </div>
  );
}
